import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

interface LightPageHeroProps {
  badge: string;
  heading: React.ReactNode;
  subtext: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaNote?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
}

/**
 * Dark hero used on all inner corporate pages — visually consistent with CorporateHero.
 */
const LightPageHero = ({
  badge, heading, subtext,
  ctaLabel, ctaHref, ctaNote,
  secondaryLabel, secondaryHref,
}: LightPageHeroProps) => {
  const [ctaHover, setCtaHover] = useState(false);
  const [secHover, setSecHover] = useState(false);

  return (
    <section
      style={{
        background: 'linear-gradient(160deg, #0a0118 0%, #1e0a3c 40%, #0d1b4b 70%, #0a0118 100%)',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: 120,
        paddingBottom: 80,
        borderBottom: 'none',
        outline: 'none',
      }}
    >
      {/* Grid overlay */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          backgroundImage: `
            linear-gradient(rgba(99,102,241,0.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99,102,241,0.07) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Glow circle — top-right */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 65%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Glow circle — bottom-left */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -60,
          left: -60,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 65%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Content */}
      <div className="container mx-auto px-6 text-center" style={{ position: 'relative', zIndex: 10 }}>

        {/* Badge */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(99,102,241,0.15)',
            border: '1px solid rgba(99,102,241,0.35)',
            color: '#a5b4fc',
            borderRadius: 100,
            padding: '6px 16px',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase' as const,
            marginBottom: 24,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#818cf8', display: 'inline-block' }} />
            {badge}
          </span>
        </motion.div>

        {/* Heading */}
        <motion.h1
          initial="hidden" animate="visible" variants={fadeUp} custom={1}
          style={{
            color: '#FFFFFF',
            fontSize: 'clamp(36px, 5vw, 64px)',
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-0.025em',
            marginBottom: 20,
          }}
        >
          {heading}
        </motion.h1>

        {/* Subtext */}
        <motion.p
          initial="hidden" animate="visible" variants={fadeUp} custom={2}
          style={{
            color: '#94a3b8',
            fontSize: 18,
            lineHeight: 1.7,
            maxWidth: 600,
            margin: '0 auto',
            marginBottom: ctaLabel ? 36 : 0,
          }}
        >
          {subtext}
        </motion.p>

        {/* CTAs */}
        {(ctaLabel || secondaryLabel) && (
          <motion.div
            initial="hidden" animate="visible" variants={fadeUp} custom={3}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 8 }}
          >
            {ctaLabel && ctaHref && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                {ctaHref.startsWith('http') ? (
                  <a href={ctaHref} target="_blank" rel="noopener noreferrer">
                    <button
                      onMouseEnter={() => setCtaHover(true)}
                      onMouseLeave={() => setCtaHover(false)}
                      style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '14px 28px',
                        borderRadius: 10,
                        fontWeight: 600,
                        fontSize: 15,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        boxShadow: ctaHover
                          ? '0 0 36px rgba(99,102,241,0.7), 0 4px 16px rgba(0,0,0,0.3)'
                          : '0 0 24px rgba(99,102,241,0.5), 0 4px 16px rgba(0,0,0,0.3)',
                        transition: 'all 0.25s ease',
                      }}
                    >
                      {ctaLabel}
                      <ArrowRight size={16} />
                    </button>
                  </a>
                ) : (
                <Link to={ctaHref}>
                  <button
                    onMouseEnter={() => setCtaHover(true)}
                    onMouseLeave={() => setCtaHover(false)}
                    style={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '14px 28px',
                      borderRadius: 10,
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      boxShadow: ctaHover
                        ? '0 0 36px rgba(99,102,241,0.7), 0 4px 16px rgba(0,0,0,0.3)'
                        : '0 0 24px rgba(99,102,241,0.5), 0 4px 16px rgba(0,0,0,0.3)',
                      transition: 'all 0.25s ease',
                    }}
                  >
                    {ctaLabel}
                    <ArrowRight size={16} />
                  </button>
                </Link>
                )}
                {ctaNote && (
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{ctaNote}</span>
                )}
              </div>
            )}
            {secondaryLabel && secondaryHref && (
              <Link to={secondaryHref}>
                <button
                  onMouseEnter={() => setSecHover(true)}
                  onMouseLeave={() => setSecHover(false)}
                  style={{
                    background: secHover ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: 'white',
                    padding: '14px 28px',
                    borderRadius: 10,
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                  }}
                >
                  {secondaryLabel}
                </button>
              </Link>
            )}
          </motion.div>
        )}
      </div>

      {/* SVG wave divider → #F8FAFC */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, lineHeight: 0, zIndex: 11, marginBottom: '-2px' }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%', border: 'none', outline: 'none' }}>
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" fill="#F8FAFC" />
        </svg>
      </div>
    </section>
  );
};

export default LightPageHero;
